import {
  DEFAULT_LARGE_SERIES_THRESHOLD,
  PROGRESSIVE_DATA_COUNT_THRESHOLD,
  SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import {
  ChartOption,
  DEFAULT_X_AXIS_OPTIONS,
  DEFAULT_Y_AXIS_OPTIONS
} from "entities/charts/options";
import _zip from "lodash/zip";
import { AxisType, SeriesType } from "types/echarts";
import { EvaChartStates } from "types/factory";
import getLogMinFromSeries from "utils/chart/getLogMinFromSeries";

import { DataEnum, Format } from "models";

import { getCountOfSeriesCollectionData } from "components/charts/utils";

import { createChartLocks } from "../locks";
import { LINE_OF_BEST_FIT_Z_INDEX } from "./constants";
import { buildYearQuarterAxisConfig } from "./dateUtils";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import { formatChartTitleWithSubscript, getDefaultYAxisLogOptions } from "./utils";

export function translateCrossPlotResponseToOptions(states: Partial<EvaChartStates>) {
  const {
    title,
    series,
    uwis,
    layout,
    uwiCoordinates,
    chartTypeParam,
    logAxis,
    overrideSeries = true,
    chartType,
    product,
    entityKind,
    getSeriesStyle,
    getHighlightedPointStyle,
    typeWellSeries = []
  } = states;

  if (series.length === 0 || !chartType) {
    return {};
  }
  const seriesCollectionDataCount = getCountOfSeriesCollectionData(series, "x");
  const isLargeSeries =
    seriesCollectionDataCount > PROGRESSIVE_DATA_COUNT_THRESHOLD ||
    (seriesCollectionDataCount > PROGRESSIVE_DATA_COUNT_THRESHOLD * 0.5 &&
      series.length < 5);

  const { lockedYMax, lockedYMin, lockedXMax, lockedXMin } = createChartLocks({
    type: EvaChart.CrossPlot,
    states
  });

  const yAxis: Partial<YAXisOption> = {
    name: formatChartTitleWithSubscript(layout.yAxis.title),
    nameTruncate: { maxWidth: 520, ellipsis: "..." } // TODO chart: use getAxisTitleTruncatingWidth
  };

  const enableHighlight = series.length < DEFAULT_LARGE_SERIES_THRESHOLD;

  const xDataType = chartTypeParam?.properties?.x?.dataType;
  const yDataType = chartTypeParam?.properties?.y?.dataType;

  const isYearMonth = layout.xAxis.title.includes("(Year-Month)");
  const isYearQuarter = layout.xAxis.title.includes("(Year-Qtr)");

  const xComputedMin = lockedXMin ?? Math.floor(layout.xAxis.min);
  const xComputedMax = lockedXMax ?? Math.ceil(layout.xAxis.max);
  const isIntegerSinglePoint =
    xDataType === DataEnum.Integer && xComputedMin === xComputedMax;

  const yComputedMin = lockedYMin ?? Math.floor(layout.yAxis.min);
  const yComputedMax = lockedYMax ?? Math.ceil(layout.yAxis.max);
  const isYIntegerSinglePoint =
    yDataType === DataEnum.Integer && yComputedMin === yComputedMax;

  const {
    axisLabelSpacing,
    quarterToMonth,
    yearQuarterAxisMin,
    yearQuarterAxisMax,
    axisLabelFormatter
  } = buildYearQuarterAxisConfig(
    series,
    isYearQuarter,
    xDataType,
    lockedXMin,
    lockedXMax
  );

  if (logAxis?.properties?.y?.active) {
    const logSettings = getDefaultYAxisLogOptions(
      layout,
      lockedYMin,
      lockedYMax,
      EvaChart.CrossPlot,
      product,
      entityKind
    );
    Object.assign(yAxis, logSettings);
    // Cross plots don't get a backend-provided layout.yAxis.logMin (unlike
    // rate/cum charts), so the shared helper falls back to the fixed 0.0001
    // floor. ECharts won't auto-range a log axis from scatter data on its own
    // (it defaults to [1, 10]), so when there's no explicit lock we derive the
    // floor ourselves: the largest power of 10 at or below the smallest
    // positive y value in the series.
    if (lockedYMin === undefined && layout.yAxis.logMin == null) {
      const dataDrivenLogMin = getLogMinFromSeries(series);
      if (dataDrivenLogMin !== undefined) {
        yAxis.min = dataDrivenLogMin;
      }
    }
  } else if (yDataType === DataEnum.Date) {
    yAxis.type = "time";
  } else if (yDataType === DataEnum.Number || yDataType === DataEnum.Integer) {
    yAxis.min = lockedYMin;
    yAxis.max = lockedYMax;
    if (chartTypeParam?.properties?.y?.property?.startsWith("Dates")) {
      yAxis.min = lockedYMin ?? Math.floor(layout.yAxis.min);
      yAxis.max = lockedYMax ?? Math.ceil(layout.yAxis.max);
    }
    if (yDataType === DataEnum.Integer) {
      Object.assign(yAxis, { minInterval: 1 });
      yAxis.min = isYIntegerSinglePoint ? yComputedMin - 1 : yAxis.min;
      yAxis.max = isYIntegerSinglePoint ? yComputedMax + 1 : yAxis.max;
    }
  } else {
    yAxis.type = "value";
  }

  yAxis.axisLabel = {
    ...DEFAULT_Y_AXIS_OPTIONS.axisLabel,
    ...(yDataType === DataEnum.Integer
      ? {
          formatter: isYIntegerSinglePoint
            ? (value) => (value === yComputedMin ? value.toString() : "")
            : (value) => value.toString()
        }
      : {})
  };
  const xAxis = {
    name: formatChartTitleWithSubscript(layout.xAxis.title),
    ...(xDataType === DataEnum.Date || xDataType === DataEnum.Text
      ? {
          type: AxisType.Time,
          min: yearQuarterAxisMin,
          max: yearQuarterAxisMax,
          ...(isYearQuarter && {
            minInterval: axisLabelSpacing,
            maxInterval: axisLabelSpacing
          })
        }
      : xDataType === DataEnum.Number
      ? {
          min: lockedXMin ?? Math.floor(layout.xAxis.min),
          max: lockedXMax ?? Math.ceil(layout.xAxis.max)
        }
      : xDataType === DataEnum.Integer
      ? {
          min: isIntegerSinglePoint ? xComputedMin - 1 : xComputedMin,
          max: isIntegerSinglePoint ? xComputedMax + 1 : xComputedMax,
          minInterval: 1
        }
      : {
          type: AxisType.Category,
          ...(layout.xAxis.data.length > 0 && { data: layout.xAxis.data }) // Only include 'data' if it has a length
        }),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    lock: {
      show: true, // it manages if lock should be shown or not
      min: {
        value:
          xDataType === DataEnum.Date || xDataType === DataEnum.Text
            ? layout.xAxis.min
            : Math.floor(layout.xAxis.min),
        type: xDataType,
        format:
          xDataType === DataEnum.Date
            ? Format.Day
            : isYearMonth
            ? Format.Month
            : xDataType === DataEnum.Text
            ? Format.YearQuarter
            : undefined
      },
      max: {
        value:
          xDataType === DataEnum.Date || xDataType === DataEnum.Text
            ? layout.xAxis.max
            : Math.ceil(layout.xAxis.max),
        type: xDataType,
        format:
          xDataType === DataEnum.Date
            ? Format.Day
            : isYearMonth
            ? Format.Month
            : xDataType === DataEnum.Text
            ? Format.YearQuarter
            : undefined
      }
    },
    axisLabel: {
      ...DEFAULT_X_AXIS_OPTIONS.axisLabel,
      ...(xDataType === DataEnum.Integer
        ? {
            formatter: isIntegerSinglePoint
              ? (value: number) => (value === xComputedMin ? value.toString() : "")
              : (value: number) => value.toString()
          }
        : isYearQuarter
        ? {
            formatter: axisLabelFormatter,
            hideOverlap: true
          }
        : {})
    }
  };

  const colorList = {};
  if (Array.isArray(series)) {
    series.forEach((s) => {
      colorList[s.label] = s.style.hexColor;
    });
  }

  const options = createEntityState(ChartOption.Chart, {
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    chartType,
    xAxis: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      ...createEntityState(ChartOption.XAxis, xAxis)
    },
    yAxis: {
      ...createEntityState(ChartOption.YAxis, {
        ...yAxis,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: yDataType !== DataEnum.Text, // it manages if lock should be shown or not
          min: { value: Math.floor(layout.yAxis.min), type: yDataType },
          max: { value: Math.ceil(layout.yAxis.max), type: yDataType }
        },
        axisPointer: {
          label: {
            margin: SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
          }
        }
      })
    },
    uwiList: uwis,
    uwiCoordinates: uwiCoordinates,
    // Conditionally include the scatter series if overrideSeries is true
    ...(overrideSeries && {
      series: [
        ...series.map((s, index) => {
          // Modified to include the new series
          const { label, style, y, uwiIndices } = s;

          // Convert quarter format to date format for time axis
          let x = s.x;
          if (isYearQuarter && xDataType === DataEnum.Text) {
            x = x.map((value) => {
              if (typeof value === "string" && value.includes("-Q")) {
                const [year, quarter] = value.split("-");
                const monthDay = quarterToMonth[quarter];
                if (monthDay) {
                  return `${year}-${monthDay}`;
                }
              }
              return value;
            });
          }

          // to debug, see EChart Debugging doc
          const zippedData = _zip(x, y, uwiIndices);

          // const data = Float64Array.from(zippedData.flat());
          const data =
            xDataType == DataEnum.Date || xDataType == DataEnum.Text
              ? zippedData
              : Float64Array.from(zippedData.flat());

          const type = s.mode === "markers" ? "scatter" : s.mode;

          if (type === SeriesType.Scatter) {
            const { opacity: pointOpacityValue, size: pointSizeValue } =
              getHighlightedPointStyle();

            return createEntityState(ChartOption.ScatterSeries, {
              id: `${label}, ${index}`,
              name: label,
              data,
              dimensions: ["xDim", "yDim", "uwiIndex"],
              encode: { x: "xDim", y: "yDim" },
              itemStyle: {
                color: s.isForecast ? "none" : style.hexColor,
                borderColor: s.isForecast ? style.hexColor : "none",
                opacity: pointOpacityValue
              },
              symbolSize: pointSizeValue,
              largeThreshold: isLargeSeries
                ? DEFAULT_LARGE_SERIES_THRESHOLD
                : seriesCollectionDataCount + 1,
              ...(isLargeSeries && { large: false, progressive: 0 }) // if super large series, go back to old chart behavior
            });
          } else if (type === SeriesType.Line) {
            const { thickness, opacity } = getSeriesStyle(label, style);

            return createEntityState(ChartOption.LineSeries, {
              id: `${label}, ${index}`,
              name: label,
              data: zippedData,
              itemStyle: { color: style?.hexColor ?? "#000" },
              lineStyle: {
                width: thickness,
                color: style?.hexColor,
                opacity: opacity
              },
              z: LINE_OF_BEST_FIT_Z_INDEX
            });
          }
        })
      ]
    }),
    enableHighlight
  });

  // Add type well performance points if available
  if (typeWellSeries.length > 0) {
    return updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }

  return options;
}
